/**
 * CasePathShell — Phase 2 stage-rail shell for the Case Path surface.
 *
 * Pure props-driven composition. NO data fetching, NO auth hooks. Renders:
 *   1. A sticky 56px header: business name + event, headline VerifiedValue +
 *      verification meter, and an Attest button gated by stageGateSatisfied.
 *   2. A 6-stage rail (setup → foundation → method → calculate → attest →
 *      deliver) with the current stage emphasized (aria-current="step") and
 *      each stage's gate state rendered via StateChip.
 *   3. A body: a main slot (children) plus a collapsible 360px-wide right
 *      rail that mounts whatever nodes are handed to it (railCards,
 *      agentTrail) — the shell does not import RailCards/AgentTrail itself.
 *
 * Role lenses (GAP-P2-ROLE-LENSES, optional `role` prop): when a role is
 * supplied, `stageLensFor(role)` (frontend/src/hooks/useRoleCapabilities.ts)
 * splits the 6 stages into "operates" (normal styling) vs everything else
 * (muted — opacity + dim token, never hidden). Current-stage emphasis
 * (isCurrent / aria-current) ALWAYS wins over lens muting — a role never
 * mutes the stage the case is actually on. When `role` is omitted, or
 * resolves to the neutral lens (empty operates AND empty observes — unknown
 * roles fail closed to neutral, not all-muted), NO lens styling is applied
 * at all: the rail renders byte-identical to the pre-lens implementation.
 *
 * Design Law: docs/plans/2026-07-24-k3-black-orange-design-law.md
 * All colors use CSS var tokens (no raw hex literals, no inline fontFamily).
 */

import React, { useState } from 'react';
import VerifiedValue, { ProvenanceKind } from '../shared/VerifiedValue';
import { StateChip, ChipState } from '../shared/StateChip';
import { stageGateSatisfied, CaseSpineState, Stage } from '../../lib/casePath';
import { stageLensFor } from '../../hooks/useRoleCapabilities';

/** Fixed 6-stage progression. casePath.ts does not export this ordering. */
const STAGES: Stage[] = ['setup', 'foundation', 'method', 'calculate', 'attest', 'deliver'];

const STAGE_LABELS: Record<Stage, string> = {
  setup: 'Setup',
  foundation: 'Foundation',
  method: 'Method',
  calculate: 'Calculate',
  attest: 'Attest',
  deliver: 'Deliver',
};

const RAIL_WIDTH = 360;

/** Guards against an unparseable `at` timestamp — never renders "Invalid Date". */
function formatSignedOffDate(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

/** Maps ChipState to abbreviated chip text. Unmapped states fall back to the raw state value. */
const CHIP_STATE_LABEL: Record<string, string> = {
  verified: 'done',
  'system-active': 'current',
  blocker: 'blocked',
  'needs-human': 'review',
  warning: 'warn',
  dormant: 'pending',
};

export interface CasePathShellProps {
  /** Client business name — headline of the sticky header. */
  businessName: string;
  /** Event descriptor text, e.g. "Fire loss — March 2023 · HARTFORD". */
  eventLabel: string;
  /** Headline dollar figure, rendered through VerifiedValue. */
  headlineValue: number | null | undefined;
  /** Provenance of the headline figure. Defaults to 'E' (engine/deterministic). */
  headlineProvenance?: ProvenanceKind;
  /**
   * OPTIONAL (CT-P P.3). First-party property RCV — the property engine row's
   * flattened output_value, client-side only. When a finite number, the header
   * renders a second labeled figure ("Property · RCV") beside the BI headline
   * as a SEPARATE recovery track (never summed with the BI settlement).
   * Absent/null → byte-identical single-pillar layout.
   */
  propertyRcv?: number | null;
  /** Count of engines that have run. */
  enginesRun: number;
  /** Count of engines expected to run. */
  enginesExpected: number;
  /** Count of outstanding blockers. */
  blockers: number;
  /** Case spine state — drives current-stage emphasis and the Attest gate. */
  state: CaseSpineState;
  /** Extra gate inputs passed through to stageGateSatisfied. */
  gateInputs: Record<string, unknown>;
  /** Fired when the Attest button is clicked while the gate is satisfied. */
  onAttest: () => void;
  /** Per-stage StateChip mapping (gate state for each of the 6 stages). */
  stageChips: Record<Stage, ChipState>;
  /**
   * OPTIONAL (GAP-P4C-ATTEST). When set, the case has already been signed
   * off: the header renders "Signed off · {name} · {date}" (tokens only)
   * in place of the Attest button — the button is never shown alongside a
   * signed state. Absent/null -> unchanged pre-existing button rendering
   * (disabled-while-gate-unsatisfied / enabled-while-satisfied).
   */
  signedOffBy?: { name: string; at: string } | null;
  /**
   * OPTIONAL workspace role driving the stage-rail lens (GAP-P2-ROLE-LENSES).
   * Absent (undefined) -> no lens styling, identical to pre-lens rendering.
   * See `stageLensFor` in `hooks/useRoleCapabilities.ts` for the matrix.
   */
  role?: string | null;
  /** Right-rail content — the shell mounts these nodes without importing them. */
  railCards: React.ReactNode;
  agentTrail: React.ReactNode;
  /** Main content slot. */
  children?: React.ReactNode;
}

export function CasePathShell({
  businessName,
  eventLabel,
  headlineValue,
  headlineProvenance = 'E',
  propertyRcv = null,
  enginesRun,
  enginesExpected,
  blockers,
  state,
  gateInputs,
  onAttest,
  stageChips,
  signedOffBy = null,
  role,
  railCards,
  agentTrail,
  children,
}: CasePathShellProps) {
  const [railOpen, setRailOpen] = useState(true);

  // ATTEST-READINESS (eyes-on finding 2026-07-25): the header button must answer "can this
  // case be attested NOW", not "is the CURRENT stage's exit gate open" — at stage 'calculate'
  // the current-stage gate passes and the button rendered enabled under 2 open blockers,
  // contradicting its own caption. Evaluate the ATTEST gate always; additionally require the
  // case to have reached the attest stage before the action unlocks.
  const attestGate = stageGateSatisfied({ ...state, stage: 'attest' }, gateInputs);
  const atAttestStage = state.stage === 'attest' || state.stage === 'deliver';
  const gate = !atAttestStage
    ? { satisfied: false, reason: `Stage is ${STAGE_LABELS[state.stage]} — attesting unlocks at the Attest stage` }
    : attestGate;

  // CT-P P.3 — dual-pillar hero: a finite propertyRcv surfaces a SECOND labeled
  // track (Property · RCV) beside the BI settlement headline. When absent, the
  // header renders byte-identical to the single-pillar layout.
  const hasProperty = typeof propertyRcv === 'number' && Number.isFinite(propertyRcv);

  // Lens is only computed when `role` is actually supplied (not undefined /
  // null) — this keeps the role-absent path from ever touching the lens
  // machinery, per (1)'s hard requirement. `lensActive` further guards the
  // NEUTRAL lens (both operates and observes empty — role omitted OR
  // unrecognized) so an unknown role behaves identically to no role at all,
  // rather than reading as "mute everything".
  const lens = role ? stageLensFor(role) : null;
  const lensActive = !!lens && (lens.operates.length > 0 || lens.observes.length > 0);

  return (
    <div
      className="font-sans"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'var(--color-bg-base)',
        color: 'var(--color-text-primary)',
      }}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Header — sticky, 56px */}
      {/* ---------------------------------------------------------------- */}
      <header
        data-testid="case-path-header"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          padding: '0 20px',
          background: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {/* Left — business name + event */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
          <span
            className="font-serif"
            data-testid="business-name"
            style={{ fontSize: 15, lineHeight: 1.15, color: 'var(--color-text-primary)', whiteSpace: 'nowrap' }}
          >
            {businessName}
          </span>
          <span
            className="font-sans"
            data-testid="event-label"
            style={{ fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
          >
            {eventLabel}
          </span>
        </div>

        {/* Center-left — headline figure(s) + verification meter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {hasProperty ? (
            <>
              {/* Track 1 — BI. headlineValue is the BI track figure (fair_settlement);
                  the label makes that explicit so it can never absorb the property figure. */}
              <div
                data-testid="bi-track-figure"
                style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}
              >
                <span
                  className="font-mono"
                  style={{ fontSize: 9, lineHeight: 1.2, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-dim)', whiteSpace: 'nowrap' }}
                >
                  BI · Settlement
                </span>
                <VerifiedValue value={headlineValue} provenance={headlineProvenance} status="fresh" size="md" />
              </div>

              {/* Track separator — hard divider, never a "+" and never a total. This
                  is the structural anti-sum device between two dollar figures, so it
                  uses the STRONG neutral rule (the "ledger rule above totals" token,
                  visible on dark) — it must be seen, not merely exist (K3 P2). */}
              <span
                aria-hidden="true"
                data-testid="track-divider"
                style={{ width: 1, height: 28, background: 'var(--color-border-strong)', flexShrink: 0 }}
              />

              {/* Track 2 — Property RCV. Separate recovery track; same engine
                  provenance grammar ('E'), smaller size for hierarchy. RCV is gross
                  replacement cost — the "· RCV" basis qualifier is mandatory and may
                  never be dropped or relabeled "Property Value / Claim". */}
              <div
                data-testid="property-rcv-figure"
                title="First-party property track — replacement cost value (RCV), engine output. A separate recovery track from the BI settlement; the two are never summed."
                aria-label={`Property replacement cost value ${(propertyRcv as number).toLocaleString('en-US')} dollars. Separate recovery track — not part of the BI settlement.`}
                style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}
              >
                <span
                  className="font-mono"
                  style={{ fontSize: 9, lineHeight: 1.2, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-dim)', whiteSpace: 'nowrap' }}
                >
                  Property · RCV
                </span>
                <VerifiedValue value={propertyRcv} provenance="E" status="fresh" size="sm" />
              </div>
            </>
          ) : (
            <VerifiedValue value={headlineValue} provenance={headlineProvenance} status="fresh" size="md" />
          )}
          <span
            className="font-mono"
            data-testid="verification-meter"
            style={{ fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
          >
            {/* Meter counts the 12 BI-attestable engines only (property is not in
                ATTESTABLE_ENGINE_IDS). In dual-pillar mode, qualify it "BI run" so it
                cannot read as covering both tracks (K3 flag 4). */}
            {enginesRun}/{enginesExpected} {hasProperty ? 'BI ' : ''}run · {blockers} blocker{blockers === 1 ? '' : 's'}
          </span>
        </div>

        {/* Right — Attest button, OR (GAP-P4C-ATTEST) the signed-off label once
            signedOffBy is set. Three states pinned: unsigned+gate-unsatisfied
            (disabled button), unsigned+gate-satisfied (enabled button),
            signedOffBy set (signed label, no button — never both at once). */}
        {signedOffBy ? (
          <span
            data-testid="signed-off-label"
            className="font-mono"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Signed off · {signedOffBy.name} · {formatSignedOffDate(signedOffBy.at)}
          </span>
        ) : (
          <>
            <button
              type="button"
              data-testid="attest-button"
              disabled={!gate.satisfied}
              title={gate.reason}
              onClick={() => {
                if (gate.satisfied) onAttest();
              }}
              style={{
                height: 32,
                padding: '0 16px',
                borderRadius: 6,
                border: 'none',
                flexShrink: 0,
                background: gate.satisfied ? 'var(--color-brand)' : 'var(--color-bg-elevated)',
                color: gate.satisfied ? 'var(--color-on-brand)' : 'var(--color-text-dim)',
                fontSize: 12,
                fontWeight: 600,
                cursor: gate.satisfied ? 'pointer' : 'not-allowed',
              }}
            >
              Attest
            </button>
            {!gate.satisfied && (
              <span
                data-testid="attest-blocked-reason"
                style={{ fontSize: 10, color: 'var(--color-text-muted)', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }}
                title={gate.reason}
              >
                {gate.reason}
              </span>
            )}
          </>
        )}
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* Stage rail — 6 stages */}
      {/* ---------------------------------------------------------------- */}
      <nav
        aria-label="Case path stages"
        data-testid="stage-rail"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '10px 20px',
          background: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-sub)',
        }}
      >
        {STAGES.map((stage, idx) => {
          const isCurrent = stage === state.stage;
          // Current-stage emphasis ALWAYS wins over lens styling: a stage
          // can only be muted when it is NOT the current stage. "Operated"
          // stages (or any stage when the lens isn't active) render normal;
          // everything else the lens doesn't operate gets muted.
          const isMuted = lensActive && !isCurrent && !lens!.operates.includes(stage);
          return (
            <React.Fragment key={stage}>
              <div
                data-testid={`stage-${stage}`}
                data-muted={isMuted ? 'true' : 'false'}
                aria-current={isCurrent ? 'step' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 8px',
                  borderRadius: 6,
                  background: isCurrent ? 'var(--color-bg-elevated)' : 'transparent',
                  opacity: isMuted ? 0.55 : 1,
                }}
              >
                <span
                  className="font-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCurrent
                      ? 'var(--color-text-primary)'
                      : isMuted
                        ? 'var(--color-text-dim)'
                        : 'var(--color-text-muted)',
                  }}
                >
                  {STAGE_LABELS[stage]}
                </span>
                <StateChip
                  state={stageChips[stage]}
                  text={CHIP_STATE_LABEL[stageChips[stage]] ?? stageChips[stage]}
                  size="sm"
                />
              </div>
              {idx < STAGES.length - 1 && (
                <span
                  aria-hidden="true"
                  style={{ width: 16, height: 1, background: 'var(--color-border-sub)', flexShrink: 0 }}
                />
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* ---------------------------------------------------------------- */}
      {/* Body — main slot + collapsible right rail */}
      {/* ---------------------------------------------------------------- */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <main data-testid="case-path-main" style={{ flex: 1, minWidth: 0, padding: 20 }}>
          {children}
        </main>

        <button
          type="button"
          data-testid="rail-toggle"
          aria-expanded={railOpen}
          aria-label={railOpen ? 'Collapse case rail' : 'Expand case rail'}
          onClick={() => setRailOpen((o) => !o)}
          style={{
            width: 20,
            flexShrink: 0,
            border: 'none',
            borderLeft: '1px solid var(--color-border)',
            background: 'var(--color-bg-surface)',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          {railOpen ? '›' : '‹'}
        </button>

        {railOpen && (
          <aside
            data-testid="case-path-rail"
            style={{
              width: RAIL_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              padding: 16,
              background: 'var(--color-bg-surface)',
              borderLeft: '1px solid var(--color-border)',
              overflowY: 'auto',
            }}
          >
            <div data-testid="rail-cards-slot">{railCards}</div>
            <div data-testid="agent-trail-slot">{agentTrail}</div>
          </aside>
        )}
      </div>
    </div>
  );
}

export default CasePathShell;
